import React, { useEffect, useState } from 'react'
import Button from '@material-ui/core/Button'
import Grid from '@material-ui/core/Grid'
import { useHistory } from 'react-router-dom'

import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
} from '@material-ui/core'

import {
  appService,
  confirmationService,
  subscriptionService,
} from '../../../../../services'
import { Editing, LoadingButton, StepHint } from '../../../../../components'
import { constants, pendingSubmissionStorage } from '../../../../../utils'

import { Main } from '../../../../../components/Layout'
import { Buttons } from '../../../../../components/Steps'
import SubscriptionStatusModal from '../../../../../components/SubscriptionStatusModal'

import { Address, Agreed, Intent, Quiz, Responsible, Student } from './elements'

const Confirmation = ({ data, prev, retrieveSubscriptionData, setStep }) => {
  const history = useHistory()

  const [agreed, setAgreed] = useState(false)
  const [jid, setJid] = useState(null)
  const [loading, setLoading] = useState(false)
  const [openFailed, setOpenFailed] = useState(false)
  const [openRenewEnrollment, setOpenRenewEnrollment] = useState(false)
  const [openSubscriptionChecker, setOpenSubscriptionChecker] = useState(false)
  const [questions, setQuestions] = useState([])
  const [statusModalOpen, setStatusModalOpen] = useState(false)

  const intentQuestions = () => questions.filter(i => i.kind === 'school')
  const quizQuestions = () => questions.filter(i => i.kind === 'subscription')

  const buttonTitle = () => (data.uuid ? 'atualização' : 'inscrição')

  const tryToGetQuestions = async () => {
    setLoading(true)

    const {
      success,
      payload: { questions: _questions },
    } = await confirmationService.getQuestions()

    if (success) {
      setQuestions(_questions)
    }

    setLoading(false)
  }

  useEffect(() => {
    tryToGetQuestions()
  }, [])

  useEffect(() => {
    if (appService.shouldRedirectToHome('subscribe')) {
      history.push(constants.routes.home.path)
    }
  }, [])

  const tryToUpdateSubscription = async () => {
    setLoading(true)

    const { success } = await subscriptionService.tryToSubmit(data)

    setLoading(false)

    if (success) {
      retrieveSubscriptionData()
    } else {
      setOpenFailed(true)
    }
  }

  const submitAndFollowJob = async (cancelRenewEnrollment = false) => {
    const { success, jid } = await subscriptionService.tryToSubmit(
      cancelRenewEnrollment ? { ...data, cancelRenewEnrollment } : data
    )

    if (!success || !jid) {
      setOpenFailed(true)
      return
    }

    pendingSubmissionStorage.setJid(jid)
    setJid(jid)
    setStatusModalOpen(true)
  }

  const submitSubscription = async () => {
    if (data.uuid) return tryToUpdateSubscription()

    const { success, payload } =
      await subscriptionService.getSubscriptionAndEnrollment(
        data.student.cpf,
        data.student.birthdate
      )

    if (success) {
      if (payload?.subscription) {
        setOpenSubscriptionChecker(true)
      } else if (payload?.reenrollment) {
        setOpenRenewEnrollment(true)
      } else {
        await submitAndFollowJob()
      }
    } else {
      setOpenFailed(true)
    }
  }

  const tryToSubmitSubscription = async () => {
    setOpenRenewEnrollment(false)

    await submitAndFollowJob(true)
  }

  const handleStatusModalClose = () => {
    setStatusModalOpen(false)
  }

  const handleStatusModalSuccess = () => {
    pendingSubmissionStorage.clearJid()
    setStatusModalOpen(false)
  }

  const handleStatusModalRenewEnrollmentRequested = () => {
    pendingSubmissionStorage.clearJid()
    setStatusModalOpen(false)
    setOpenRenewEnrollment(true)
  }

  const handleStatusModalError = () => {
    pendingSubmissionStorage.clearJid()
    setStatusModalOpen(false)
  }

  const handleStatusModalRetry = () => {
    setStatusModalOpen(false)
  }

  const handleCloseSubscriptionChecker = () => {
    setOpenSubscriptionChecker(false)
  }

  const handleCloseFailed = () => {
    setOpenFailed(false)
  }

  const handleCloseRenewEnrollment = () => {
    setOpenRenewEnrollment(false)

    history.push({ pathname: constants.routes.home.path })
  }

  const redirectToSearchSubscription = () => {
    history.push({ pathname: constants.routes.subscriptions.search.path })
  }

  return (
    <>
      <StepHint>
        Verifique se todos os dados foram preenchidos corretamente, caso alguma
        informação esteja incorreta, realize as alterações necessárias voltando
        para a etapa anterior
      </StepHint>

      <Main>
        <Editing />

        <Grid container spacing={1}>
          <Student student={data.student} />
          <Address address={data.address} />
          <Responsible responsible={data.responsible} student={data.student} />
          <Intent intent={data.intent} questions={intentQuestions()} />
          <Quiz questions={quizQuestions()} quiz={data.quiz} />
          <Agreed agreed={agreed} setAgreed={setAgreed} />
        </Grid>
      </Main>

      <Buttons>
        <Button
          color="primary"
          disabled={loading}
          onClick={prev}
          variant="contained"
        >
          Voltar
        </Button>

        <LoadingButton
          disabled={loading || !agreed}
          loading={loading}
          onClick={submitSubscription}
        >
          {`Confirmar ${buttonTitle()}`}
        </LoadingButton>
      </Buttons>

      <Dialog
        open={openSubscriptionChecker}
        onClose={handleCloseSubscriptionChecker}
      >
        <DialogContent dividers>
          <DialogContentText>
            {
              'Foi identificada uma solicitação de Pré-matrícula para este mesmo CPF'
            }
          </DialogContentText>
        </DialogContent>

        <DialogActions>
          <Button color="primary" onClick={redirectToSearchSubscription}>
            Clique aqui para consultar esta inscrição
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openRenewEnrollment} onClose={handleCloseRenewEnrollment}>
        <DialogContent dividers>
          <DialogContentText>
            {
              'Foi identificada uma solicitação de Rematrícula para este CPF. Declaro que estou ciente que, caso prossiga com a pré-matrícula, a rematrícula será cancelada.'
            }
          </DialogContentText>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseRenewEnrollment} disabled={loading}>
            Desistir
          </Button>

          <Button color="primary" onClick={tryToSubmitSubscription}>
            Prosseguir
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openFailed} onClose={handleCloseFailed}>
        <DialogContent dividers>
          <DialogContentText>
            {
              'Não foi possível concluir a solicitação no momento. Tente novamente e, caso o problema persista, entre em contato com a SRE de sua região.'
            }
          </DialogContentText>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseFailed} disabled={loading}>
            Fechar e tentar novamente
          </Button>
        </DialogActions>
      </Dialog>

      {statusModalOpen && (
        <SubscriptionStatusModal
          jid={jid}
          open={statusModalOpen}
          setStep={setStep}
          retrieveSubscriptionData={retrieveSubscriptionData}
          onClose={handleStatusModalClose}
          onSuccess={handleStatusModalSuccess}
          onError={handleStatusModalError}
          onFailed={pendingSubmissionStorage.clearJid}
          onRenewEnrollmentRequested={handleStatusModalRenewEnrollmentRequested}
          onRetry={handleStatusModalRetry}
        />
      )}
    </>
  )
}

export default Confirmation
